import { useState } from 'react'

import { InlineVideoPlayer } from 'components/agents/InlineVideoPlayer'
import { VideoSegmentStrip } from 'components/agents/VideoSegmentStrip'
import styles from 'components/chat-components/AgentMessage.module.css'

import type { ContentRendererProperties, VideoSegmentContent } from 'components/chat-components/AgentMessage.types'
import type { ReactElement } from 'react'

type VideoSegmentRendererProperties = {
  content: VideoSegmentContent
} & Pick<ContentRendererProperties, 'mux' | 'videoDuration' | 'videoSources' | 'videoUrl'>

type ResolvedVideoSegmentPlayback = {
  duration: number | undefined
  mux: VideoSegmentRendererProperties['mux']
  videoUrl: string | undefined
}

const isContentPending = (content: VideoSegmentContent): boolean => {
  return 'pending' in content && content.pending === true
}

const resolveVideoSegmentPlayback = ({
  content,
  mux,
  videoDuration,
  videoSources,
  videoUrl,
}: VideoSegmentRendererProperties): ResolvedVideoSegmentPlayback => {
  const requestedFileId = content.fileId?.trim()
  const selectedSource = requestedFileId
    ? videoSources?.find(source => source.fileId === requestedFileId)
    : videoSources?.find(source => source.isPrimary)

  if (requestedFileId) {
    return {
      duration: selectedSource?.videoDuration,
      mux: selectedSource?.mux,
      videoUrl: selectedSource?.videoUrl,
    }
  }

  return {
    duration: selectedSource?.videoDuration ?? videoDuration,
    mux: selectedSource?.mux ?? mux,
    videoUrl: selectedSource?.videoUrl ?? videoUrl,
  }
}

export const VideoSegmentRenderer = ({
  content,
  mux,
  videoUrl,
  videoDuration,
  videoSources,
}: VideoSegmentRendererProperties): ReactElement => {
  /* --- Section 2: Local State --- */
  const [currentPlaybackTime, setCurrentPlaybackTime] = useState<number | undefined>(undefined)

  /* --- Section 3: Derived Values --- */
  const isPending = isContentPending(content)
  const hasSegment = Boolean(content.segment.startTime && content.segment.endTime)
  const playback = resolveVideoSegmentPlayback({ content, mux, videoDuration, videoSources, videoUrl })
  const isPlayerReady = !isPending && hasSegment && playback.videoUrl !== undefined

  /* --- Section 6: Render --- */
  return (
    <div className={styles.contentVideoSegment}>
      <div className={styles.videoSegmentFrame}>
        {isPlayerReady && playback.videoUrl ? (
          <InlineVideoPlayer
            videoUrl={playback.videoUrl}
            mux={playback.mux}
            startTime={content.segment.startTime}
            endTime={content.segment.endTime}
            isLoop={content.loop ?? true}
            onTimeUpdate={setCurrentPlaybackTime}
            className={styles.videoSegmentPlayer}
          />
        ) : (
          <div className={styles.videoSegmentSkeleton} aria-busy="true" aria-label="Loading clip">
            <span className={styles.videoSegmentSpinner} />
          </div>
        )}
        {isPlayerReady && playback.videoUrl ? (
          <VideoSegmentStrip
            mux={playback.mux}
            startTime={content.segment.startTime}
            endTime={content.segment.endTime}
            videoDuration={playback.duration}
            currentPlaybackTime={currentPlaybackTime}
          />
        ) : undefined}
      </div>
      <div className={styles.videoSegmentRange}>
        {hasSegment ? `${content.segment.startTime}–${content.segment.endTime}` : ' '}
      </div>
    </div>
  )
}
